// Mock session data for the North Star prototype — stands in for what the AI
// pipeline + eligibility engine would return. Copy lives in NorthStar.content.ts.

// The scripted answers Keni "hears", keyed to TRIAGE_QUESTIONS by index. Used so
// the transcript reads like a real chat when you tap through with quick replies
// (a free-text answer overrides the matching entry at runtime).
export const SCRIPTED_ANSWERS: string[] = [
  'My mum',
  'At home',
  'Yes, a lot',
  '£2k–£4k',
  'Yes',
];

export const CARE_COST_MONTHLY = '£4,000';
export const CLAIM_TOTAL_ANNUAL = '£11,344';

export type FoundBenefit = {
  name: string;
  annual: string; // headline annual value
  status: string; // plain-language confidence
  tone: 'good' | 'maybe';
};

// What the reveal shows — the top benefits from the PRD's starting set, with
// the human-readable confidence the eligibility engine would attach.
export const FOUND_BENEFITS: FoundBenefit[] = [
  {
    name: 'Attendance Allowance',
    annual: '£5,644',
    status: 'Likely eligible',
    tone: 'good',
  },
  {
    name: 'Pension Credit',
    annual: '£3,900',
    status: 'Worth checking',
    tone: 'maybe',
  },
  {
    name: 'Council Tax Reduction',
    annual: '£1,800',
    status: 'Likely eligible',
    tone: 'good',
  },
];

// The Claims hub: one list of everything on the go, each with where it's up to.
// Statuses run not-started → in-progress → submitted → awarded.
export type ApplicationStatus =
  'Not started' | 'In progress' | 'Ready to submit' | 'Submitted' | 'Awarded';

export type Application = {
  name: string;
  annual: string;
  status: ApplicationStatus;
};

export const APPLICATIONS: Application[] = [
  { name: 'Attendance Allowance', annual: '£5,644', status: 'Ready to submit' },
  { name: 'Pension Credit', annual: '£3,900', status: 'In progress' },
  { name: 'Council Tax Reduction', annual: '£1,800', status: 'Submitted' },
];

// How a task gets done: capture a photo, answer a question, invite someone, or
// review collected details — each opens a drawer. `claim` is the exception: it
// opens the full Attendance Allowance claim form rather than a drawer.
export type TaskKind = 'claim' | 'capture' | 'questions' | 'invite' | 'review';

export type NextTask = {
  id: string;
  title: string;
  detail: string;
  icon: string;
  // What this task is part of, shown at the top of its drawer.
  from: string;
  kind: TaskKind;
  // A line of drawer context, and the footer button label.
  body: string;
  cta: string;
};

// The triage-driven task list — the "actions reduced to a task list" idea. The
// Attendance Allowance claim is the first task: starting it runs the full form,
// which walks the member through the details it needs. The rest are the smaller
// bits Keni still needs, or ones pulled out because they're quick.
export const NEXT_TASKS: NextTask[] = [
  {
    id: 'aa-claim',
    title: 'Start Mum’s Attendance Allowance claim',
    detail: '64 questions — Keni fills most of it from what you’ve told her',
    icon: 'file-lines',
    from: 'For Mum’s Attendance Allowance',
    kind: 'claim',
    body: '',
    cta: 'Start claim',
  },
  {
    id: 'prescription',
    title: 'Photo Mum’s prescription',
    detail:
      'For her Attendance Allowance — gets her NHS number and meds in one shot',
    icon: 'camera',
    from: 'For Mum’s Attendance Allowance',
    kind: 'capture',
    body: 'One photo of the label gets Mum’s NHS number and full medicine list — no typing.',
    cta: 'Open camera',
  },
  {
    id: 'ni',
    title: 'Confirm Mum’s National Insurance number',
    detail: 'Needed for Pension Credit',
    icon: 'id-card',
    from: 'For Mum’s Pension Credit',
    kind: 'questions',
    body: 'Keni needs this to check and apply for Pension Credit.',
    cta: 'Save',
  },
  {
    id: 'circle',
    title: 'Add your sister to Mum’s care circle',
    detail: 'So you can share the load and keep each other updated',
    icon: 'user-plus',
    from: 'Your care circle',
    kind: 'invite',
    body: 'Invite someone to share the load — they’ll see Mum’s plan and can help with tasks.',
    cta: 'Send invite',
  },
  {
    id: 'review',
    title: 'Review what Keni collected',
    detail: 'Check and edit Mum’s details before anything is submitted',
    icon: 'pen-to-square',
    from: 'Your details',
    kind: 'review',
    body: 'Check these are right before anything is submitted. Tap any to edit.',
    cta: 'Looks right',
  },
];

// What Keni gathered in the triage — shown in the "review" task drawer.
export const COLLECTED_DETAILS: { label: string; value: string }[] = [
  { label: 'Who', value: 'Mum' },
  { label: 'Lives', value: 'At home' },
  { label: 'Daily help', value: 'Yes, a lot' },
  { label: 'Monthly cost', value: '£2k–£4k' },
  { label: 'Over pension age', value: 'Yes' },
];
